import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface LoginRequest {
    user: string;
    password: string;
    baseAcessoId?: number;
}

export interface AvailableBase {
    id: number;
    descricao: string;
}

export interface Empresa {
    id: number;
    descricao: string;
}

export interface LoginResponse {
    success: boolean;
    message: string;
    token?: string;
    userId?: number;
    userName?: string;
    approvalUserlOS?: boolean;
    baseAcessoId?: number;
    baseAcessoDescricao?: string;
    empresaId?: number;
    empresaDescricao?: string;
    requiresBaseSelection?: boolean;
    availableBases?: AvailableBase[];
    availableEmpresas?: Empresa[];
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private readonly apiUrl = environment.urlApi ? `${environment.urlApi}/auth` : '/ats-resulth-car-api-mobile/auth';

    constructor(private http: HttpClient) { }

    login(user: string, password: string, baseAcessoId?: number): Observable<LoginResponse> {
        const request: LoginRequest = { user, password, baseAcessoId };
        return this.http.post<LoginResponse>(`${this.apiUrl}/login`, request).pipe(
            tap(response => {
                if (response.success && response.token) {
                    this.persistSession(response);
                }
                // Store available bases whenever the server sends them (base-selection step)
                if (response.availableBases && response.availableBases.length > 0) {
                    localStorage.setItem('available_bases', JSON.stringify(response.availableBases));
                }
            })
        );
    }

    switchBase(baseAcessoId: number): Observable<LoginResponse> {
        const token = localStorage.getItem('auth_token') ?? '';
        const options = { headers: new HttpHeaders({ Authorization: `Bearer ${token}` }) };
        return this.http.post<LoginResponse>(`${this.apiUrl}/switch-base`, { baseAcessoId }, options).pipe(
            tap(response => {
                if (response.success && response.token) {
                    this.persistSession(response);
                }
                if (response.availableBases && response.availableBases.length > 0) {
                    localStorage.setItem('available_bases', JSON.stringify(response.availableBases));
                }
            })
        );
    }

    switchEmpresa(empresaId: number): Observable<LoginResponse> {
        const token = localStorage.getItem('auth_token') ?? '';
        const options = { headers: new HttpHeaders({ Authorization: `Bearer ${token}` }) };
        return this.http.post<LoginResponse>(`${this.apiUrl}/switch-empresa`, { empresaId }, options).pipe(
            tap(response => {
                if (response.success && response.token) {
                    this.persistSession(response);
                }
                if (response.availableEmpresas && response.availableEmpresas.length > 0) {
                    localStorage.setItem('available_empresas', JSON.stringify(response.availableEmpresas));
                }
            })
        );
    }

    logout(): void {
        this.clearSession();
    }

    private persistSession(response: LoginResponse): void {
        localStorage.setItem('auth_token', response.token!);
        if (response.userName) {
            localStorage.setItem('user_name', response.userName);
        }
        if (response.userId !== undefined) {
            localStorage.setItem('user_id', response.userId!.toString());
        }
        if (response.baseAcessoId !== undefined) {
            localStorage.setItem('base_acesso_id', response.baseAcessoId!.toString());
        }
        if (response.baseAcessoDescricao) {
            localStorage.setItem('base_acesso_descricao', response.baseAcessoDescricao);
        }
        if (response.empresaId !== undefined) {
            localStorage.setItem('empresa_id', response.empresaId!.toString());
        }
        if (response.empresaDescricao) {
            localStorage.setItem('empresa_descricao', response.empresaDescricao);
        }
        if (response.availableEmpresas && response.availableEmpresas.length > 0) {
            localStorage.setItem('available_empresas', JSON.stringify(response.availableEmpresas));
        }
        localStorage.setItem('approval_user_os', String(!!response.approvalUserlOS));
    }

    isLoggedIn(): boolean {
        const token = this.getToken();
        if (!this.isTokenValid(token)) {
            this.clearSession();
            return false;
        }

        return true;
    }

    getToken(): string | null {
        return localStorage.getItem('auth_token');
    }

    getUserName(): string | null {
        return localStorage.getItem('user_name');
    }

    getUserId(): number | null {
        const id = localStorage.getItem('user_id');
        return id ? parseInt(id, 10) : null;
    }

    getBaseAcessoId(): number | null {
        const id = localStorage.getItem('base_acesso_id');
        return id ? parseInt(id, 10) : null;
    }

    getBaseAcessoDescricao(): string | null {
        return localStorage.getItem('base_acesso_descricao');
    }

    getAvailableBases(): AvailableBase[] {
        try {
            const raw = localStorage.getItem('available_bases');
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    }

    getEmpresaId(): number | null {
        const id = localStorage.getItem('empresa_id');
        return id ? parseInt(id, 10) : null;
    }

    getEmpresaDescricao(): string | null {
        return localStorage.getItem('empresa_descricao');
    }

    canApproveOs(): boolean {
        return localStorage.getItem('approval_user_os') === 'true';
    }

    getAvailableEmpresas(): Empresa[] {
        try {
            const raw = localStorage.getItem('available_empresas');
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    }

    private authHeaders(): HttpHeaders {
        const token = this.getToken();
        return new HttpHeaders({ Authorization: `Bearer ${token ?? ''}` });
    }

    clearSession(): void {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_name');
        localStorage.removeItem('user_id');
        localStorage.removeItem('base_acesso_id');
        localStorage.removeItem('base_acesso_descricao');
        localStorage.removeItem('empresa_id');
        localStorage.removeItem('empresa_descricao');
        localStorage.removeItem('available_bases');
        localStorage.removeItem('available_empresas');
        localStorage.removeItem('approval_user_os');
    }

    private isTokenValid(token: string | null): boolean {
        if (!token) {
            return false;
        }

        try {
            const payload = this.parseJwtPayload(token);
            const exp = Number(payload['exp']);

            if (!Number.isFinite(exp)) {
                return false;
            }

            return exp * 1000 > Date.now();
        } catch {
            return false;
        }
    }

    private parseJwtPayload(token: string): Record<string, unknown> {
        const parts = token.split('.');
        if (parts.length !== 3) {
            throw new Error('Invalid JWT format');
        }

        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const normalized = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
        const json = atob(normalized);
        return JSON.parse(json) as Record<string, unknown>;
    }
}
